import { DataPoint, QuestionInterface, Rule } from '../types';
import { ProcedureRecord, ProcedureRecords, UnresolvedAutoQuery } from '../context/types';
import initialize, {
  EngineRule,
  parseAndExtractVariableNamesFromCondition,
  initializeNotifications,
  isAnyRulesActive,
  turnOffAlertRules,
  turnOffAllRulesForProcedure,
  turnOffSelectionAlertRules,
  turnOnAllRulesForProcedure,
  turnOnRulesForQuestion,
  turnOnSelectionAlertRulesForQuestion,
} from './logic/ProcedureLogicUtil';
import VariableNameAnswers from '../context/types/VariableNameAnswers';
import { RuleEffects } from '../hooks/useProcedureRules.types';
import { evaluateAsync } from './promiseUtil';
import { RuleType } from '../enums';

/**
 * Arguments required for the {@link toggleQuestions} utility function.
 * @public
 */
export interface ToggleQuestionsArgs {
  /** The map of all procedure records. */
  records: ProcedureRecords;
  /** Variable names of questions to toggle the disabled state for. */
  variableNames: string[];
  /** All available question definitions (used for variableName -> questionId lookup). */
  questions: QuestionInterface[];
  /** ID of the specific record to modify within `records`. */
  recordId: string;
  /** `true` to enable questions (set is_disabled: false), `false` to disable (set is_disabled: true). */
  enable: boolean;
}

/**
 * Arguments required for the {@link createRulesEngine} utility function.
 * @public
 */
export interface CreateRulesEngineArgs {
  /** Rule definitions for the procedure. */
  rules: Rule[];
  /** Identifier for the overall procedure. */
  procedureId: string;
  /** Identifier for the specific record this engine instance applies to. */
  recordId: string;
  /** Callback triggered when a rule's alert condition is met. */
  onAlertTriggered: (alertText: string, autoQueryText: string | undefined, recordId: string, variableNames: string[]) => void;
  /** Callback triggered when a rule dictates questions should be enabled. */
  onEnableTriggered: (variableNames: string[], recordId: string) => void;
  /** Callback triggered when a rule dictates questions should be disabled. */
  onDisableTriggered: (variableNames: string[], recordId: string) => void;
}

export interface CreateNotificationsEngineArgs {
  /** Rule definitions for the procedure. */
  rules: Rule[];
  /** Identifier for the overall procedure. */
  procedureId: string;
  /** Identifier for the specific record this engine instance applies to. */
  recordId: string;
  notificationHandler: Function;
}

/**
 * Creates and initializes a rules engine instance for a specific record context.
 *
 * @remarks
 * Maps rule definitions to the engine's configuration format and connects
 * rule actions (alerts, enable/disable) to the provided callbacks.
 *
 * @param rules - Rule definitions for the procedure.
 * @param procedureId - Identifier for the overall procedure.
 * @param recordId - Identifier for the specific record this engine instance applies to.
 * @param onAlertTriggered - Callback triggered when a rule's alert condition is met.
 * @param onEnableTriggered - Callback triggered when a rule dictates questions should be enabled.
 * @param onDisableTriggered - Callback triggered when a rule dictates questions should be disabled.
 * @returns An initialized rules engine instance (type 'any' for compatibility reasons with the underlying `initialize` function).
 * @public
 */
export function createRulesEngine({
  rules,
  procedureId,
  recordId,
  onAlertTriggered,
  onEnableTriggered,
  onDisableTriggered,
}: CreateRulesEngineArgs): any {
  const currentRules = rules || []; // Ensure rules is an array, default to empty
  const engineConfig: Array<EngineRule> = currentRules.map((rule) => {
    const referencedVariableNames = parseAndExtractVariableNamesFromCondition(rule.condition);
    // Ensure disableQuestions is always an array and lowercase
    const disableQuestions = rule.disableQuestions?.map((q) => q.toLowerCase()) ?? [];

    return {
      ruleId: rule.ruleId,
      condition: rule.condition,
      alertText: rule.alertText,
      // Pass autoQueryText so alertHandler can access it
      autoQueryText: rule.autoQueryText,
      disableQuestions: disableQuestions.length === 0 ? undefined : disableQuestions,
      procedureId, // Pass procedureId for engine context
      ruleType: rule.ruleType,
      // --- Handlers ---
      alertHandler: () => {
        // Trigger callback if alertText or autoQueryText is present
        if (rule.alertText || rule.autoQueryText) {
          onAlertTriggered(
            rule.alertText ?? '', // Default to empty string
            rule.autoQueryText,
            recordId,
            referencedVariableNames,
          );
        }
      },
      disableHandler: () => {
        // Trigger callback only if there are questions to disable
        if (disableQuestions.length > 0) {
          onDisableTriggered(disableQuestions, recordId);
        }
      },
      enableHandler: () => {
        // Trigger callback only if there are questions to enable (uses same list)
        if (disableQuestions.length > 0) {
          onEnableTriggered(disableQuestions, recordId);
        }
      },
    };
  });
  // Initialize and return the engine with the generated configuration
  return initialize(engineConfig);
}

export function createNotificationsEngine({
  rules,
  procedureId,
  recordId,
  notificationHandler,
}: CreateNotificationsEngineArgs) {
  if (!rules) { rules = []; }
  return initializeNotifications(
    rules
      ? rules
        .filter(({ ruleType }) => ruleType === RuleType.ALERT || ruleType === RuleType.SELECTION_ALERT)
        .map((rule: Rule) => ({
          ruleId: rule.ruleId,
          ruleType: RuleType.NOTIFICATION,
          condition: rule.condition,
          alertText: rule.alertText,
          procedureId,
          notificationHandler: () => notificationHandler(rule, recordId),
        }))
      : [],
  );
}

/**
 * Arguments required for the {@link runEvaluationSequence} utility function.
 * @public
 */
export interface RunEvaluationSequenceArgs {
  /** The initialized rules engine instance. */
  engine: any;
  notificationsEngine: any;
  /** Current state of answers { variableName: answer }. */
  justAnswers: VariableNameAnswers;
  /** Variable names identifying the questions/rules targeted by this evaluation run. */
  variableNames: string[];
  /** ID of the procedure (used for turning off all rules). */
  procedureId: string;
  /** Optional: Answers map representing a specific selection change, used for selection-based alerts. */
  selectionAnswers?: VariableNameAnswers;
  /** Optional: `true` if evaluation is for pre-populated data (disables standard alerts). Defaults to `false`. */
  isPrepopulated?: boolean;
  /** Optional: Should we only run the notification rules?. */
  isOnlyNotificationRun?: boolean
  effectsCollectorRef?: React.MutableRefObject<RuleEffects>;
}

/**
 * Executes the standard rule evaluation sequence using the provided engine and answers.
 *
 * @remarks
 * This involves:
 * 1. Turning relevant rules on.
 * 2. Potentially turning off alert rules (for pre-population) or selection alerts (temporarily).
 * 3. Running a first evaluation pass (for disable/enable, standard alerts).
 * 4. Potentially running a second evaluation pass (for selection-specific alerts).
 * 5. Turning off all rules and signaling completion via `setProcessing(false)`.
 *
 * @param engine - The initialized rules engine instance.
 * @param justAnswers - Current state of answers { variableName: answer }.
 * @param variableNames - Variable names identifying the questions/rules targeted by this evaluation run.
 * @param procedureId - ID of the procedure (used for turning off all rules).
 * @param setProcessing - Callback to update processing/loading state (e.g., in UI).
 * @param selectionAnswers - Optional: Answers map representing a specific selection change, used for selection-based alerts.
 * @param isPrepopulated - Optional: `true` if evaluation is for pre-populated data (disables standard alerts). Defaults to `false`.
 * @returns void
 * @public
 */
export async function runEvaluationSequence({
  engine,
  notificationsEngine,
  justAnswers,
  variableNames,
  procedureId,
  selectionAnswers,
  isPrepopulated = false,
  isOnlyNotificationRun = false,
  effectsCollectorRef,
}: RunEvaluationSequenceArgs): Promise<void> {
  if (!effectsCollectorRef) {
    console.warn('No effectsCollectorRef found.');
    return;
  }
  // *** Clear the collector at the beginning of the sequence ***
  effectsCollectorRef.current = {
    alerts: [], notificationAlerts: [], autoQueries: [], variablesToEnable: { }, variablesToDisable: {},
  };

  turnOnAllRulesForProcedure(notificationsEngine, procedureId);

  // TODO: split this notification run out so that we don't block running the
  // actual rules. These two engines can be completely independent
  await evaluateAsync(notificationsEngine, justAnswers);

  if (isOnlyNotificationRun) {
    return;
  }

  // --- Setup Phase ---
  variableNames.forEach((variableName) => turnOnRulesForQuestion(engine, variableName.toLowerCase()));

  if (isPrepopulated) {
    turnOffAlertRules(engine);
  } else {
    // Temporarily turn off selection alerts for the first pass
    turnOffSelectionAlertRules(engine);
  }

  try {
    // --- First Evaluation Pass ---
    await evaluateAsync(engine, justAnswers);

    // Turn off all rules after the main pass by default
    turnOffAllRulesForProcedure(engine, procedureId);

    // --- Second Evaluation Pass (Selection Alerts, if applicable) ---
    // Only run if NOT pre-populated, selectionAnswers exist, and any rule was active before turning all off
    if (!isPrepopulated && selectionAnswers) {
      // Re-enable selection alert rules ONLY for the specifically changed variables
      variableNames.forEach((variableName) => turnOnSelectionAlertRulesForQuestion(engine, variableName.toLowerCase()));
      if (isAnyRulesActive(engine)) {
        await evaluateAsync(engine, selectionAnswers); // Evaluate against selection facts
        turnOffAllRulesForProcedure(engine, procedureId);
      }
    }
  } catch (e) {
    console.error('Error during rule evaluation sequence:', e);
  } finally {
    turnOffAllRulesForProcedure(engine, procedureId);
  }
}

/**
 * Arguments required for the {@link clearAutoQueriesFromList} utility function.
 * @public
 */
export interface ClearAutoQueriesArgs {
  /** The current array of unresolved auto-queries. */
  currentList: UnresolvedAutoQuery[];
  /** The variable name associated with the auto-queries to clear. */
  variableNameToClear: string;
  /** The specific procedure/record ID context for the queries to clear. */
  recordId?: string;
}

/**
 * Filters an array of `UnresolvedAutoQuery`, removing entries that match
 * both the specified `variableNameToClear` and `recordId`.
 *
 * @param currentList - The current array of unresolved auto-queries.
 * @param variableNameToClear - The variable name associated with the auto-queries to clear.
 * @param recordId - The specific procedure/record ID context for the queries to clear.
 * @returns A new array instance with matching queries removed.
 * @public
 */
export function clearAutoQueriesFromList({
  currentList,
  variableNameToClear,
  recordId,
}: ClearAutoQueriesArgs): UnresolvedAutoQuery[] {
  // Filter the list, keeping items that DON'T match the criteria
  return currentList.filter(
    (query) =>
      // Check if variableNames includes the one to clear
      !query.variableNames?.includes(variableNameToClear)
      // OR check if the recordId context doesn't match
      || query.procedureOrRecordId !== recordId,
  );
}

/**
 * Arguments required for the {@link computeToggleAllUpdate} utility function.
 * @public
 */
export interface ComputeToggleAllUpdateArgs {
  /** The current procedure record. */
  currentRecordData: ProcedureRecord;
  /** Question definitions affected by the toggle operation. */
  questions: QuestionInterface[];
  /** The answer value to set (if `toggleOn` is true) or remove (if `toggleOn` is false and multi-select). */
  newAnswer: string;
  /** All possible answer options (needed for sorting multi-select answers correctly). */
  availableAnswerOptions: string[];
  /** `true` to add/set the answer, `false` to remove/clear it. */
  toggleOn: boolean;
  /** Optional: `true` if the target questions are multi-select. */
  isMultiSelectQuestion?: boolean;
}

/**
 * Describes a calculated value change that was applied to a DataPoint.
 * @public
 */
export interface AppliedChangeInfo {
  /** The question ID of the DataPoint that was changed. */
  questionId: string;
  /** The variable name of the DataPoint that was changed. */
  variableName: string;
  /** The full, updated DataPoint object after the calculation was applied. */
  newDataPoint: DataPoint;
}

/**
 * Result of computing and applying direct calculations via {@link computeAndApplyDirectCalculatedValues}.
 * @public
 */
export interface ComputeApplyResult {
  /** The data map after applying all direct calculations triggered by the target variable change. `{ [qId]: DataPoint }`. */
  finalDataMap: ProcedureRecord;
  /** List of changes that were actually applied (i.e., where the calculated value differed from the existing value). */
  appliedChanges: AppliedChangeInfo[];
}
